import React, { useState, useRef } from 'react';
import {
    Container,
    Typography,
    Box,
    Grid,
    TextField,
    Button,
    Paper,
    Card,
    CardContent,
    Snackbar,
    Alert,
    CircularProgress
} from '@mui/material';
import './Contact.scss';

// Add TypeScript declaration for EmailJS
declare global {
    interface Window {
        emailjs: {
            send: (
                serviceId: string,
                templateId: string,
                templateParams: Record<string, any>,
                userId: string
            ) => Promise<any>;
        };
    }
}

const Contact: React.FC = () => {
    const formRef = useRef<HTMLFormElement>(null);
    const [formState, setFormState] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });

    const [errors, setErrors] = useState({
        name: false,
        email: false,
        message: false
    });

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' as 'success' | 'error'
    });

    const [loading, setLoading] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormState(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user types
        if (errors[name as keyof typeof errors]) {
            setErrors(prev => ({
                ...prev,
                [name]: false
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {
            name: formState.name.trim() === '',
            email: !/^\S+@\S+\.\S+$/.test(formState.email),
            message: formState.message.trim() === ''
        };

        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (validateForm()) {
            setLoading(true);

            try {
                // Option 1: Use EmailJS (add the script to public/index.html)
                // <script type="text/javascript" src="https://cdn.emailjs.com/sdk/2.3.2/email.min.js"></script>
                if (window.emailjs) {
                    const templateParams = {
                        name: formState.name,
                        email: formState.email,
                        subject: formState.subject || 'Contact Form Submission',
                        message: formState.message,
                        to_email: 'contact@planetbinc.com'
                    };

                    await window.emailjs.send(
                        'YOUR_SERVICE_ID', // Create an EmailJS account and get these values
                        'YOUR_TEMPLATE_ID',
                        templateParams,
                        'YOUR_USER_ID'
                    );

                    // Show success message
                    setSnackbar({
                        open: true,
                        message: 'Thank you for your message! We will get back to you soon.',
                        severity: 'success'
                    });

                    // Reset form
                    setFormState({
                        name: '',
                        email: '',
                        subject: '',
                        message: ''
                    });
                } else {
                    // Option 2: You can also use a form submission service like Formspree
                    const form = formRef.current;
                    const formData = new FormData(form!);

                    const response = await fetch('https://formspree.io/f/YOUR_FORMSPREE_ID', {
                        method: 'POST',
                        body: formData,
                        headers: {
                            Accept: 'application/json'
                        }
                    });

                    if (response.ok) {
                        // Show success message
                        setSnackbar({
                            open: true,
                            message: 'Thank you for your message! We will get back to you soon.',
                            severity: 'success'
                        });

                        // Reset form
                        setFormState({
                            name: '',
                            email: '',
                            subject: '',
                            message: ''
                        });
                    } else {
                        throw new Error('Failed to send message');
                    }
                }
            } catch (error) {
                console.error('Error sending form:', error);
                setSnackbar({
                    open: true,
                    message: 'Failed to send message. Please try again or contact us directly at contact@planetbinc.com',
                    severity: 'error'
                });
            } finally {
                setLoading(false);
            }
        } else {
            setSnackbar({
                open: true,
                message: 'Please correct the errors in the form.',
                severity: 'error'
            });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({
            ...prev,
            open: false
        }));
    };

    const contactInfo = [
        {
            title: 'Email',
            content: 'contact@planetbinc.com',
            icon: '✉️'
        },
        {
            title: 'Phone',
            content: '+1 (555) 123-4567',
            icon: '📞'
        },
        {
            title: 'Address',
            content: '123 App Street, Digital City, 94105',
            icon: '📍'
        }
    ];

    return (
        <div className="contact-page">
            {/* Hero Section */}
            <Box className="hero-section">
                <Container maxWidth="lg">
                    <Typography variant="h3" component="h1" gutterBottom>
                        Contact Us
                    </Typography>
                    <Typography variant="h6">
                        Have questions or feedback? We'd love to hear from you! Fill out the form below or contact us directly at contact@planetbinc.com.
                    </Typography>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ my: 6 }}>
                <Grid container spacing={4}>
                    {/* Contact Form */}
                    <Grid item xs={12}>
                        <Paper elevation={2} className="contact-form-container">
                            <Typography variant="h4" component="h2" gutterBottom>
                                Send Us a Message
                            </Typography>

                            <form ref={formRef} onSubmit={handleSubmit}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Your Name"
                                            name="name"
                                            variant="outlined"
                                            value={formState.name}
                                            onChange={handleChange}
                                            error={errors.name}
                                            helperText={errors.name ? 'Name is required' : ''}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Email Address"
                                            name="email"
                                            type="email"
                                            variant="outlined"
                                            value={formState.email}
                                            onChange={handleChange}
                                            error={errors.email}
                                            helperText={errors.email ? 'Please enter a valid email' : ''}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Subject"
                                            name="subject"
                                            variant="outlined"
                                            value={formState.subject}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Your Message"
                                            name="message"
                                            variant="outlined"
                                            multiline
                                            rows={6}
                                            value={formState.message}
                                            onChange={handleChange}
                                            error={errors.message}
                                            helperText={errors.message ? 'Message is required' : ''}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            size="large"
                                            className="submit-button"
                                            disabled={loading}
                                        >
                                            {loading ? <CircularProgress size={24} /> : 'Send Message'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>
                        </Paper>
                    </Grid>

                    {/* Contact Information */}
                    {/* <Grid item xs={12} md={4}>
                        <Box className="contact-info-container">
                            <Typography variant="h4" component="h3" gutterBottom>
                                Contact Information
                            </Typography>

                            {contactInfo.map((info, index) => (
                                <Card key={index} className="contact-info-card" elevation={1}>
                                    <CardContent>
                                        <Box display="flex" alignItems="center">
                                            <Box className="contact-icon">{info.icon}</Box>
                                            <Box>
                                                <Typography variant="h6" component="h4">
                                                    {info.title}
                                                </Typography>
                                                <Typography variant="body1">
                                                    {info.content}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            ))}

                            <Box className="social-media-container" mt={4}>
                                <Typography variant="h6" gutterBottom>
                                    Follow Us
                                </Typography>
                                <Box display="flex" gap={2}>
                                    <Button variant="outlined" className="social-button">Twitter</Button>
                                    <Button variant="outlined" className="social-button">Facebook</Button>
                                    <Button variant="outlined" className="social-button">Instagram</Button>
                                </Box>
                            </Box>
                        </Box>
                    </Grid> */}
                </Grid>
            </Container>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default Contact;